/**
 * @fileoverview Week-view multi-day events row — top strip across all 7
 *               weekdays for multi-day events / all-day Morgan key dates.
 *               Ported as-is from upstream.
 */

import { useMemo } from "react";
import {
  parseISO,
  startOfDay,
  startOfWeek,
  endOfWeek,
  addDays,
  differenceInDays,
  isBefore,
  isAfter,
} from "date-fns";

import { MonthEventBadge } from "@/calendar/components/month-view/month-event-badge";

import type { IEvent } from "@/calendar/interfaces";

interface IProps {
  selectedDate: Date;
  multiDayEvents: IEvent[];
}

export function WeekViewMultiDayEventsRow({
  selectedDate,
  multiDayEvents,
}: IProps) {
  const weekStart = startOfWeek(selectedDate);
  const weekEnd = endOfWeek(selectedDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const processedEvents = useMemo(() => {
    return multiDayEvents
      .map((event) => {
        const start = parseISO(event.startDate);
        const end = parseISO(event.endDate);
        const adjustedStart = isBefore(start, weekStart) ? weekStart : start;
        const adjustedEnd = isAfter(end, weekEnd) ? weekEnd : end;
        const startIndex = differenceInDays(adjustedStart, weekStart);
        const endIndex = differenceInDays(adjustedEnd, weekStart);

        return {
          ...event,
          adjustedStart,
          adjustedEnd,
          startIndex,
          endIndex,
        };
      })
      .sort((a, b) => {
        const startDiff = a.adjustedStart.getTime() - b.adjustedStart.getTime();
        if (startDiff !== 0) return startDiff;
        return b.endIndex - b.startIndex - (a.endIndex - a.startIndex);
      });
  }, [multiDayEvents, weekStart, weekEnd]);

  const eventRows = useMemo(() => {
    const rows: (typeof processedEvents)[] = [];

    processedEvents.forEach((event) => {
      const targetRow = rows.find((row) =>
        row.every(
          (e) => e.endIndex < event.startIndex || e.startIndex > event.endIndex,
        ),
      );

      if (targetRow) {
        targetRow.push(event);
      } else {
        rows.push([event]);
      }
    });

    return rows;
  }, [processedEvents]);

  const hasEventsInWeek = useMemo(() => {
    return multiDayEvents.some((event) => {
      const start = parseISO(event.startDate);
      const end = parseISO(event.endDate);

      return (
        (start >= weekStart && start <= weekEnd) ||
        (end >= weekStart && end <= weekEnd) ||
        (start <= weekStart && end >= weekEnd)
      );
    });
  }, [multiDayEvents, weekStart, weekEnd]);

  if (!hasEventsInWeek) return null;

  return (
    <div className="hidden overflow-hidden sm:flex">
      <div className="w-[72px] border-b" />
      <div className="grid flex-1 grid-cols-7 divide-x border-b border-l">
        {weekDays.map((day, dayIndex) => (
          <div
            key={day.toISOString()}
            className="flex h-full flex-col gap-1 py-1"
          >
            {eventRows.map((row, rowIndex) => {
              const event = row.find(
                (e) => e.startIndex <= dayIndex && e.endIndex >= dayIndex,
              );

              if (!event) {
                return <div key={`${rowIndex}-${dayIndex}`} className="h-6" />;
              }

              const position: "first" | "middle" | "last" | "none" =
                dayIndex === event.startIndex && dayIndex === event.endIndex
                  ? "none"
                  : dayIndex === event.startIndex
                    ? "first"
                    : dayIndex === event.endIndex
                      ? "last"
                      : "middle";

              return (
                <MonthEventBadge
                  key={`${event.id}-${dayIndex}`}
                  event={event}
                  cellDate={startOfDay(day)}
                  position={position}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
